import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import {
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';

import { useAppDispatch } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import {
  TripRequestEmbedRead,
  useTripRequestEmbedListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';
import { formatRTKQueryError } from 'src/common/util/formatRTKQueryError';

import { settingSlice } from '../setting.slice';
import { TRIP_REQUEST_EMBED_KB_URL } from './constants';

export function TripRequestEmbedList() {
  const dispatch = useAppDispatch();
  const {
    data: tripRequestEmbeds,
    isLoading: isLoadingTripRequestEmbeds,
    isFetching: isFetchingTripRequestEmbeds,
    error: tripRequestEmbedsError,
  } = useTripRequestEmbedListQuery({});
  if (isLoadingTripRequestEmbeds || isFetchingTripRequestEmbeds) {
    return <LoadingIndicator />;
  }
  if (tripRequestEmbedsError) {
    return (
      <FormErrorMessage>
        {formatRTKQueryError(tripRequestEmbedsError)}
      </FormErrorMessage>
    );
  }

  return (
    <DataGrid
      tableId="trip-request-embeds"
      data={tripRequestEmbeds?.results || []}
      columns={columns}
      totalCount={tripRequestEmbeds?.count || 0}
      emptyState={
        <EmptyState
          Icon={<TruckIcon className="mx-auto h-6 w-auto text-gray-700" />}
          entityName="Trip Request Embed"
          Title={
            <>
              <h4 className="mt-3 text-gray-700">Add Trip Request Embed</h4>
              <p className="text-sm text-gray-500">
                <a
                  href={TRIP_REQUEST_EMBED_KB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 underline underline-offset-2 hover:text-indigo-500"
                >
                  Learn how to set up your first embed
                </a>
              </p>
            </>
          }
          description="No trip request embeds found, create a trip request embed to get started."
          Action={
            <Button
              variant="primary"
              onClick={() => {
                dispatch(settingSlice.actions.createTripRequestEmbed());
              }}
            >
              Add a Trip Request Embed
            </Button>
          }
        />
      }
    />
  );
}

const columnHelper = createColumnHelper<TripRequestEmbedRead>();

const columns = [
  columnHelper.accessor('label', {
    header: 'Label',
  }),
  columnHelper.accessor('domain', {
    header: 'Domain',
  }),
  columnHelper.accessor('payer_display_name', {
    header: 'Payer',
  }),
  columnHelper.accessor('description', {
    header: 'Description',
  }),
  columnHelper.accessor('is_active', {
    header: 'Is Active',
    cell: ({ row }) => <div>{row.original.is_active ? 'Yes' : 'No'}</div>,
  }),
  columnHelper.display({
    id: 'manage',
    enableHiding: false,
    header() {
      return <div className="flex flex-row">Manage</div>;
    },
    cell(props) {
      return (
        <TripRequestEmbedManageCell tripRequestEmbed={props.row.original} />
      );
    },
    meta: {
      headerWidth: 100,
    },
  }),
];

function TripRequestEmbedManageCell({
  tripRequestEmbed,
}: {
  tripRequestEmbed: TripRequestEmbedRead;
}) {
  const dispatch = useAppDispatch();

  const onEditClick = () => {
    dispatch(settingSlice.actions.openTripRequestEmbedModal(tripRequestEmbed));
  };

  const onDeleteClick = () => {
    dispatch(settingSlice.actions.deleteTripRequestEmbed(tripRequestEmbed));
  };

  const onCopyClick = () => {
    dispatch(
      settingSlice.actions.openTripRequestEmbedCopyModal(tripRequestEmbed)
    );
  };

  return (
    <div className="flex flex-row items-center justify-end">
      <Button
        dataTestId={`copy-trip-request-embed-${tripRequestEmbed.id}`}
        variant="icon"
        ariaLabel={`Copy Trip Request Embed: ${tripRequestEmbed.label}`}
        onClick={onCopyClick}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <ClipboardIcon className="mt-1 h-[20px] w-[24px]" />
          Embed
        </div>
      </Button>
      <Button
        dataTestId={`edit-trip-request-embed-${tripRequestEmbed.id}`}
        variant="icon"
        ariaLabel={`Edit Trip Request Embed: ${tripRequestEmbed.label}`}
        onClick={onEditClick}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <PencilIcon className="mt-1 h-[20px] w-[24px]" />
          Edit
        </div>
      </Button>
      <Button
        dataTestId={`delete-trip-request-embed-${tripRequestEmbed.id}`}
        variant="icon"
        ariaLabel={`Delete Trip Request Embed: ${tripRequestEmbed.label}`}
        onClick={onDeleteClick}
      >
        <div
          className="flex flex-col items-center text-xs"
          style={{ fontSize: '0.625rem' }}
        >
          <TrashIcon className="mt-1 h-[20px] w-[24px]" />
          Delete
        </div>
      </Button>
    </div>
  );
}
